import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import { isEmpty, isEqual } from "lodash";
import { createStructuredSelector } from "reselect";

import { required, requiredCodeRef, getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { getBaseUnits, getUnitOptions } from "utils/fields";
import { useStateSelector } from "utils/field";

import Checkbox from "components/Input/Checkbox";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Tooltip from "components/Tooltip";
import Field from "components/Field";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";

import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

import { actions } from "features/Model/Enclosure/_ducks";

const { setInitCode, updateCodeDrawer } = actions;
const expFloorTypeValidation = [required, ...getValidation("expFloorInsType")];

export const CodeFooter = ({ toggleDrawer, setInitExpFloorCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitExpFloorCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit, codeType }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef, codeType)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// Floor Ins. Type codes found in code library
export const floorTypeLibCodesMap = (libCodes, handleEditLibCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("Floor-U");

            return {
                label: label,
                labelIcon: Book,
                labelTag: !standardCode ? "User Defined" : "",
                labelActionComponent: () =>
                    handleEditLibCode ? (
                        <LabelActionComponent
                            codeRef={codeRef}
                            handleEdit={handleEditLibCode}
                            codeType={standardCode ? "S" : "U"}
                        />
                    ) : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Floor Ins. Type codes found in model, filtered by those in library
export const floorTypeModelCodesMap = (modelCodes, libCodes, handleEditModelCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "Floor") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "Floor";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("Floor-U");

            return {
                label,
                labelTag: !standardCode ? "User Defined" : "",
                labelActionComponent: () => (
                    <LabelActionComponent
                        codeRef={codeRef}
                        handleEdit={handleEditModelCode}
                        codeType={standardCode ? "S" : "U"}
                    />
                ),
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const Construction = React.memo(
    ({
        accessor,
        change,
        id,
        setInitExpFloorCode,
        updateCodeDrawer,
        disabledCodeEdit = false,
        canBeEditedFromParametric = true,
        isCompact = false,
        selectedUpgrade,
        isParametricAnalysis = false,
        isCommunity = false,
        codeRefs = [],
    }) => {
        const [isTouched, setIsTouched] = useState(false); // Hide warning when code is reselected but not yet added to ecmInputMap

        const { Floor: { codes: libCodes = {} } = {} } = useStateSelector(`user.codeLib`) || {};
        const { calculatingRValue = {} } = useStateSelector(`enclosure`) || {};
        const { currentPackage = {} } = useStateSelector("upgradeLibrary") || {};
        const { modelData = {} } = useStateSelector(`model`) || {};
        const { parametricAnalysis = {} } = useStateSelector(`parametricAnalysis`) || {};
        const { community = {} } = useStateSelector(`community`) || {};
        
        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};

        // Reconstruct allEcmInputMapUpgrades
        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                .map((key) =>
                    Object.entries(ecmInputMap[key])
                        .filter(([k, _]) => k !== "None")
                        .map(([k, v]) => ({ [k]: v }))
                )
                .flat()
            : [];

        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const addUserSpecifiedRVal = useStateSelector(`${accessor}.addUserSpecifiedRVal`) || false;
        const isAddUserSpecifiedRVal = isParametricAnalysis ? addUserSpecifiedRVal : false;

        const modelUnits = modelData?.uiSettings?.primaryUnits || {};

        const { codeRef: selectedCodeRef } = useStateSelector(`${accessor}.expFloorInsType`) || {};
        const selectedNomRValue = useStateSelector(`${accessor}.expFloorInsType_nomRVal`) || 0;
        const expFloorCodeWarning = useStateSelector(`${accessor}.expFloorInsType_warning`) || "";
        const rValUnits = useStateSelector(`${accessor}.expFloorInsType_u`);

        const { [id]: { expFloorInsType: isCalculatingRValue = false } = {} } = calculatingRValue;

        //Determine if selectedCodeRef is in modelCodes (attached properly)
        const modelCodesContainsSelected =
            Object.keys(modelCodes).includes(selectedCodeRef) || selectedCodeRef === "UserSpecified" || isTouched;

        const handleTypeChange = useCallback(
            ({ codeRef = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.expFloorInsType_warning`, "");
                    change(`${accessor}.expFloorInsType_nomRVal`, selectedNomRValue || 0.1);
                    change(`${accessor}.expFloorInsType_effRVal`, selectedNomRValue || 0.1);
                    return;
                }

                const modelMatch = modelCodes[codeRef] || {};
                if (!isEmpty(modelMatch)) {
                    change(`${accessor}.expFloorInsType_nomRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.expFloorInsType_effRVal`, modelMatch.nominalRValue || 0.1);
                    change(`${accessor}.expFloorInsType_warning`, modelMatch.warningType || "");
                } else {
                    const { nominalRValue = 0, warningType = "" } = libCodes[codeRef] || {};
                    change(`${accessor}.expFloorInsType_nomRVal`, nominalRValue || 0.1);
                    change(`${accessor}.expFloorInsType_effRVal`, nominalRValue || 0.1);
                    change(`${accessor}.expFloorInsType_warning`, warningType || "");

                    if (isCommunity || isParametricAnalysis) {
                        setIsTouched(true);
                    } else {
                        change(`model.modelData.codes.${codeRef}`, { ...libCodes[codeRef], codeRef });
                    }
                }
                //Redundancy
                change(`model.stashedCodes.Floor.${accessor.split(".").slice(-1)[0]}`, codeRef);
            },
            [libCodes, modelCodes, change, accessor, isCommunity, isParametricAnalysis]
        );

        const handleEditModelCode = useCallback(
            async (event, codeRef, codeType) => {
                event.stopPropagation(); // Don't let click event bubble up to parent
                const initCodeType = codeType === "U" ? "udefStandard" : "expFloorInsType";
                const code = modelCodes[codeRef];
                await setInitExpFloorCode(code, initCodeType);
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Floor",
                    isEditing: true,
                    componentType: "Floor",
                    codeType: codeType,
                    fieldAccessor: `${accessor}.expFloorInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitExpFloorCode, updateCodeDrawer]
        );

        const handleEditLibCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef, codeType) => {
                    event.stopPropagation(); // Don't let click event bubble up to parent
                    const initCodeType = codeType === "U" ? "udefStandard" : "expFloorInsType";
                    const code = { ...libCodes[codeRef], codeRef: codeRef, isLibCode: true };
                    await setInitExpFloorCode(code, initCodeType);
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Floor",
                        isEditing: true,
                        componentType: "Floor",
                        codeType: codeType,
                        fieldAccessor: `${accessor}.expFloorInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [accessor, id, libCodes, setInitExpFloorCode, updateCodeDrawer]
            );

        return (
            <>
                <InputRow gridTemplate={isCompact ? "1fr 1fr" : "31.85% 23.25%"} compact={isCompact}>
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.expFloorInsType`}
                        options={[
                            {
                                label: "User Specified",
                                value: {
                                    codeLabel: "User Specified",
                                    codeRef: "UserSpecified",
                                },
                            },
                            ...floorTypeModelCodesMap(modelCodes, libCodes, handleEditModelCode),
                            ...floorTypeLibCodesMap(libCodes, handleEditLibCode),
                        ].map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value.codeRef),
                        }))}
                        label="Floor Type"
                        placeholder="Choose Floor Type"
                        searchPlaceholder="Search Floor Codes"
                        search={true}
                        footer={() => (
                            <CodeFooter
                                toggleDrawer={(value) =>
                                    updateCodeDrawer({
                                        isOpen: value,
                                        codeName: "Floor",
                                        isEditing: false,
                                        componentType: "Floor",
                                        codeType: "S",
                                        fieldAccessor: `${accessor}.expFloorInsType`,
                                        componentId: id,
                                    })
                                }
                                setInitExpFloorCode={setInitExpFloorCode}
                            />
                        )}
                        className={classes.expFloorType}
                        isLoading={isCalculatingRValue}
                        onChange={handleTypeChange}
                        validate={requiredCodeRef}
                        disabled={!canBeEditedFromParametric}
                        compact={isCompact}
                        warning={!modelCodesContainsSelected ? "codeDataMissing" : null}
                    />
                    {selectedCodeRef === "UserSpecified" ? (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.expFloorInsType_effRVal`}
                            label={"Effective R-Value"}
                            placeholder={0.0}
                            disabled={selectedCodeRef !== "UserSpecified" || !canBeEditedFromParametric}
                            validate={expFloorTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("expFloorInsType")}
                            change={change}
                            setValue={selectedNomRValue || 0.1} // selectedNomRValue is updated to equal expFloorInsType_effRVal
                            units={{
                                base: getBaseUnits("expFloorInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.expFloorInsType_u`,
                            }}
                            onChange={(value) => {
                                if (selectedCodeRef === "UserSpecified") {
                                    change(`${accessor}.expFloorInsType_nomRVal`, value || 0.1);
                                }
                            }}
                            info={"User specified values are assumed to be effective R-values."}
                            compact={isCompact}
                        />
                    ) : (
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.expFloorInsType_nomRVal`}
                            label={"Nominal R-Value"}
                            placeholder={0.0}
                            disabled={true}
                            validate={expFloorTypeValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("expFloorInsType")}
                            change={change}
                            setValue={selectedNomRValue || 0.1}
                            units={{
                                base: getBaseUnits("expFloorInsType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: rValUnits,
                                unitType: "thermalResistance",
                                accessor: `${accessor}.expFloorInsType_u`,
                            }}
                            info={
                                "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                            }
                            warning={expFloorCodeWarning}
                            isLoading={isCalculatingRValue}
                            compact={isCompact}
                        />
                    )}
                </InputRow>
                {isCompact && (
                    <InputRow gridTemplate="1fr" compact lastRow>
                        <Field
                            className={classes.adjacentCheckbox}
                            component={Checkbox}
                            name={`${accessor}.adjacentEnclosedSpace`}
                            label="Adjacent to enclosed unconditioned space"
                            large
                            type="checkbox"
                            compact
                        />
                    </InputRow>
                )}
                {isParametricAnalysis && (
                    <>
                        <InputRow gridTemplate="1fr" compact lastRow>
                            <Field
                                className={classes.adjacentCheckbox}
                                component={Checkbox}
                                name={`${accessor}.addUserSpecifiedRVal`}
                                label="Add User Specified R-Value to All Components"
                                large
                                type="checkbox"
                                compact
                                onChange={(value) => {
                                    if (value) {
                                        change(`${accessor}.expFloorInsType`, {
                                            codeRef: "UserSpecified",
                                            codeLabel: "User Specified",
                                        });
                                    }
                                }}
                                disabled={!canBeEditedFromParametric}
                            />
                        </InputRow>
                        {isAddUserSpecifiedRVal && (
                            <div
                                style={{
                                    backgroundColor: "aliceblue",
                                    borderRadius: "10px",
                                    padding: "0.75rem",
                                    lineHeight: "normal",
                                    fontSize: "small",
                                    marginTop: "1.25rem",
                                }}
                            >
                                This user specified effective R-value will be added to all exposed floor components in
                                the model individually. The resulting exposed floor R-values may not be equal, depending
                                on their starting points.
                                <br />
                                <br />
                                The initial effective R-value for each component is determined using the parallel path
                                calculation method, aligned with F280 and the National Building Code, and may differ
                                slightly from HOT2000's calculation method.
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    setInitExpFloorCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Construction);
